import React, { useEffect } from 'react';

import { Typography, Tooltip, Dropdown, MenuButton, Menu, MenuItem, Box, Divider, Card } from '@mui/joy';

/* Icons */
import IconButton from '@mui/material/IconButton';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';

import PostStrength from './PostStrength';

import CircularProgress from '@mui/joy/CircularProgress';


const ToolBarTop = ({ toggleBold, toggleItalic, toggleBullets, toggleNumbered, content, showEmojiPicker, setShowEmojiPicker, setOpenMediaModal, handleOptimize, postStrengthLoading, postStrength, handlePostStrength, showPostStrength, setShowPostStrength }) => {

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 1, pl: 1 }}>

                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Tooltip title="Add Boldness" arrow sx={{ zIndex: 10000000 }}>
                        <IconButton onClick={toggleBold}>
                            <FormatBoldIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Add Italics" arrow sx={{ zIndex: 10000000 }}>
                        <IconButton onClick={toggleItalic}>
                            <FormatItalicIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Bullet points">
                        <IconButton onClick={toggleBullets}>
                            <FormatListBulletedIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Numbered list">
                        <IconButton onClick={toggleNumbered}>
                            <FormatListNumberedIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Add Emojis" arrow sx={{ zIndex: 10000000 }}>
                        <IconButton onClick={() => setShowEmojiPicker(!showEmojiPicker)} >
                            <EmojiEmotionsIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Add Image / Video / Pdf (carousel)" arrow sx={{ zIndex: 10000000 }}>
                        <IconButton onClick={() => setOpenMediaModal(true)}>
                            <AddAPhotoIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Optimize Post with AI" arrow sx={{ zIndex: 10000000 }}>
                        <IconButton>
                            <Dropdown>
                                <MenuButton
                                    startDecorator={<SmartToyIcon sx={{ fontSize: '20px' }} />}
                                    disabled={content?.length < 1}
                                    sx={{ border: '0px', padding: '0px', margin: '0px', minHeight: '0px' }}
                                >
                                </MenuButton>
                                <Menu placement="bottom-end">
                                    <Menu sx={{ zIndex: 10000000, maxHeight: '200px' }}>
                                        <MenuItem onClick={() => handleOptimize('COPYRIGHT_TIPS', content)}>Copywriting Tips</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('KEEP_WRITING', content)}>Keep Writing</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('ADD_EMOJIS', content)}>Add Emojis</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('SHORTER', content)}>Make Shorter</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('EXPAND', content)}>Expand Post</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('HOOKS', content)}>Create Hook</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('CREATE_CTA', content)}>Create CTA</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('IMPROVE_POST', content)}>Improve Post</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('ASSERTIVE', content)}>More Assertive</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('CASUAL', content)}>More Casual</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('FORMAL', content)}>More Formal</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('FIX_GRAMMAR', content)}>Fix Grammar</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('POST_IDEAS', content)}>Post Ideas</MenuItem>
                                    </Menu>
                                </Menu>
                            </Dropdown>
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box sx={{ position: 'relative' }}>
                    <Tooltip title="Measure Post Strength" arrow sx={{ zIndex: 10000000 }}>
                        <IconButton
                            sx={{
                                border: '1px solid #c6c3c3',
                                borderRadius: '10px',
                                padding: '2px 10px',
                                mr: 1,
                                fontSize: '12px',
                                fontWeight: 'bold',
                                color: (content?.length >= 10 && !postStrengthLoading) ? 'orange' : 'inherit', // Change icon color based on condition
                                '&:hover': {
                                    backgroundColor: (content?.length >= 10 && !postStrengthLoading) ? 'rgba(0,0,0,0.04)' : 'inherit',
                                },
                                // Apply additional styles for children based on the same condition
                                '.MuiTypography-root': {
                                    color: (content?.length >= 10 && !postStrengthLoading) ? '#3F51B5' : 'inherit',
                                },
                            }}
                            onClick={() => handlePostStrength()}
                            disabled={content?.length < 10 || postStrengthLoading}
                        >
                            <AnalyticsIcon />
                            <Typography level="body-xs" sx={{ ml: 1 }}>Post Strength</Typography>
                        </IconButton>
                    </Tooltip>

                    <Card sx={{ display: showPostStrength ? 'block' : 'none', position: 'absolute', zIndex: 10000000, top: '35px', right: '0px', width: '300px', p: 0, boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)' }}>
                        <Box sx={{ p: 1 }}>
                            {postStrengthLoading ? (
                                <>
                                    <Typography level="body-xs" sx={{ mb: 1 }}>Post Strength</Typography>
                                    <Divider />
                                    <div style={{
                                        position: 'relative',
                                        top: 0,
                                        left: 0,
                                        margin: '20px 0px',
                                        padding: '10px',
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor: 'rgba(255, 255, 255, 0.7)', // Semi-transparent white
                                        zIndex: 1000, // Ensure it's above other content
                                    }}>
                                        <CircularProgress variant="solid" />
                                        <Typography level="title-md" sx={{ marginLeft: '1rem' }}>
                                            Loading, evaluating your post strength...
                                        </Typography>
                                    </div>
                                </>
                            ) : (
                                <>

                                    <PostStrength
                                        postStrength={postStrength}
                                        setShowPostStrength={setShowPostStrength}
                                    />
                                </>
                            )}
                        </Box>
                    </Card>
                </Box>


            </Box>

            <Divider sx={{ mb: 0 }} />
        </>
    );
}

export default ToolBarTop;
